import React, { useRef, useState, useEffect } from 'react';
import landingService from '../../services/landingService';
import './StatsSection.css';

const Counter = ({ target, suffix, isVisible }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const isDecimal = target % 1 !== 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(isDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, target]);

  return (
    <span className="stats-section__number">
      {count}{suffix}
    </span>
  );
};

// Parse value like "200M+" into { number: 200, suffix: "M+" }
const parseStatValue = (val) => {
  if (typeof val === 'number') return { number: val, suffix: '' };
  const str = String(val);
  const match = str.match(/^([\d.]+)(.*)$/);
  if (match) return { number: parseFloat(match[1]), suffix: match[2] };
  return { number: 0, suffix: str };
};

const StatsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [stats, setStats] = useState([]);

  // Fetch stats from API
  useEffect(() => {
    const fetchStats = async () => {
      const result = await landingService.getPublicStats();
      if (result.success && result.stats) {
        const s = result.stats;
        setStats([
          { value: s.totalPapers || '200M+', label: 'Papers Indexed', description: 'Across every discipline and publisher worldwide' },
          { value: typeof s.totalResearchers === 'number' ? s.totalResearchers.toLocaleString() + '+' : (s.totalResearchers || '50K+'), label: 'Researchers', description: 'Active researchers collaborating daily' },
          { value: (s.totalCountries || 180) + '+', label: 'Countries', description: 'A truly global research community' },
          { value: s.platformUptime || '99.9%', label: 'Uptime', description: 'Enterprise-grade reliability you can count on' },
        ]);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`stats-section ${isVisible ? 'stats-section--visible' : ''}`}
    >
      <div className="stats-section__container">
        <div className="stats-section__header">
          <h2 className="stats-section__title">Trusted by researchers worldwide</h2>
          <p className="stats-section__subtitle">
            Join a growing community that's transforming how research is discovered, shared, and advanced.
          </p>
        </div>
        <div className="stats-section__grid">
          {stats.map((stat, i) => {
            const parsed = parseStatValue(stat.value);
            return (
              <div
                key={stat._id || stat.label}
                className="stats-section__card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <Counter target={parsed.number} suffix={parsed.suffix} isVisible={isVisible} />
                <span className="stats-section__label">{stat.label}</span>
                <span className="stats-section__desc">{stat.description}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
